// Help content imported from markdown file
// This file contains the full help documentation content

const helpContent = `# Welcome to IntakeAI Help

---

## Getting Started 🚀

### IntakeAI 101

Welcome to IntakeAI! Here's what you'll find when you open the app:

1. **Ask** - Chat with your recordings and documents to recall details, spot patterns, and generate insights
2. **Record** - Start a new recording session with our built-in audio recording tool
3. **Upload** - Upload existing audio files or documents
4. **Files** - View all your recordings, transcripts, and documents in one place
5. **Folders** - Organize your files into collections for better organization and AI analysis
6. **Templates** - Create structured workflows for different types of sessions
7. **People** - Manage contacts and track interactions
8. **Help** - Access documentation and support

### What is IntakeAI?

IntakeAI is your central hub for documents, audio recordings, and transcripts that you can query and analyze. Think of it as a smart workspace where everything you record or upload becomes searchable and analyzable.

**Built-in Audio Recording**

We've built a robust audio recording tool right into the platform—no need for external recording software. Just click record and start capturing. The system automatically transcribes your audio in real-time, identifies different speakers, and extracts key insights.

**Your Central Knowledge Base**

Once you've recorded or uploaded content, it all lives in one place. You can:
- Query across all your recordings and documents using AI chat
- Organize files into folders for better structure
- Generate summaries tailored to different audiences
- Track interactions with people across multiple sessions
- Spot patterns and trends across your entire collection

Whether you're conducting interviews, intake sessions, meetings, or any scenario where you need to capture and analyze spoken content, IntakeAI handles everything from recording to transcription to AI-powered insights—so you can focus on the conversation instead of taking notes.

---

## Recording Sessions 🎙️

### How Recording Works
When you start a recording, IntakeAI captures audio and automatically converts it to text in real-time. The system also identifies different speakers and can generate insights and summaries once the recording is complete.

### Starting a New Recording
1. Click "Start Recording" in the sidebar
2. Choose a template (optional but recommended for structured sessions)
3. Click "Start Recording" to create your session
4. Click the red record button to begin capturing audio

### Recording Controls
- **Red Circle**: Start or resume recording
- **Pause**: Temporarily pause (your speakers and context are preserved)
- **Stop**: End recording and trigger AI processing (transcription, analysis, summaries)
- **Add Audio**: Continue adding to existing recordings

### Ending and Summarizing a Session
When you're done recording, click the **Stop** button to end the session. This will:

1. **Stop the recording** and finalize the audio capture
2. **Begin transcription** - The system automatically converts your audio to text
3. **Start AI analysis** - The system identifies speakers, extracts insights, and generates key points
4. **Create summaries** - If you're using a template with summary definitions, summaries for different audiences are automatically generated

After stopping, you'll see the session status change to "In Progress" while processing, then "Analyzed" once everything is complete. You can then review the transcript, insights, and summaries in the session tabs.

### Understanding Session States
These status indicators help you track where each recording is in the process:

- **Draft**: Session created but no recording yet
- **In Progress**: Recording has started or contains recorded content
- **Analyzed**: AI has finished processing (transcription and insights are ready)
- **Complete / Incomplete**: Whether all template questions have been answered
- **Pending Approval**: Awaiting review (if your organization uses approval workflows)

### Adding to Existing Recordings
For sessions that already have content, recording controls are hidden by default to keep the interface clean. Click "Add Audio" in the session header to show the controls and continue recording.

---

## Files 📁

### What are Files?
Files are your individual recordings and sessions. Every time you record or upload audio, it becomes a file in your system. Files contain the full recording, transcript, AI insights, and any associated documents or notes.

### Viewing Your Files
Go to "Files" in the sidebar to see all your recordings in one place. You can:

- **Browse all recordings**: See every session you've created or have access to
- **Search and filter**: Find specific recordings by title, date, or content
- **View details**: Click any file to see the full transcript, insights, and related information
- **Organize**: Move files into folders to keep them organized

### File Information
Each file shows:

- **Title and date**: When the recording was made
- **Status**: Whether it's analyzed, complete, or pending approval
- **Duration**: How long the recording is
- **Speakers**: Who participated in the session
- **Collections**: Which folders the file belongs to

---

## Folders 📂

### What are Folders?
Folders (also called Collections) help you organize your recordings into groups. Think of them like folders on your computer—you can create folders for different cases, projects, clients, or any other way you want to organize your work.

### Why Use Folders?
Folders make it easier to:

- **Group related recordings**: Keep all sessions for a specific case or project together
- **Control access**: Share entire folders with specific teams or people
- **Stay organized**: Find recordings faster when they're grouped logically
- **Analyze collections**: Use AI chat to ask questions about all recordings in a folder at once

### Creating and Managing Folders
1. Go to "Folders" in the sidebar
2. Click "New Folder" to create a collection
3. Give it a name and description
4. Add recordings to folders by moving them from the Files page or when viewing a recording

### Using Folders
- **View folder contents**: Click a folder to see all recordings inside it
- **Move recordings**: Drag and drop files into folders, or use the move option from a file's menu
- **Folder-level AI chat**: Ask questions about all recordings in a folder simultaneously
- **Share folders**: Share entire collections with your team

---

## Templates 📋

### What are Templates?
Templates help you standardize your recording process by defining the questions you'll ask and how summaries should be formatted. Think of them as reusable forms for different types of sessions—whether it's a medical intake, legal interview, business meeting, or any other scenario.

Using a template ensures you capture consistent information every time and automatically generates tailored summaries for different audiences.

### How to Use Templates

**Creating a Template:**
1. Go to "Templates" in the sidebar
2. Click "New Template"
3. Add questions that guide your conversations
4. Set up summary definitions for different audiences

**Using a Template:**
When starting a new recording, select a template to automatically load its questions. As you record, you can answer these questions, and the system will generate summaries based on your template settings.

### Question Types
Choose the right question format for the information you need:

- **Short Text**: Single-line answers (names, dates, brief responses)
- **Long Text**: Multi-paragraph responses for detailed information
- **Multiple Choice**: Select one option from a predefined list
- **Yes/No**: Simple true/false or yes/no questions
- **Number**: Numeric values only

### Summary Definitions
Templates can automatically generate different summaries for different audiences. For example, you might create one summary for your legal team and another for management.

- **Distribution Groups**: Assign summaries to specific teams or departments
- **Custom Prompts**: Tell the AI what information each audience needs
- **Automatic Generation**: Summaries are created automatically when your recording ends

---

## People Management 👥

### Why Manage People?
The People feature helps you organize contacts and track all interactions with specific individuals. When you associate people with recordings, you can quickly see all sessions they've participated in, making it easier to find and review relevant conversations.

### Adding People
1. Go to "People" in the sidebar
2. Click "New Person" to add a contact
3. Fill in their details: name, email, phone, and any other relevant information

### Person Records
Each person profile contains:

- **Overview**: Contact information and a summary of their interactions
- **Recordings**: All sessions where this person participated (automatically linked when you assign them as a speaker)
- **Documents**: Files and attachments associated with this person
- **Notes**: Your personal notes and observations about them

### Linking People to Recordings
You can associate people with recordings in two ways:

- **From the People page**: Click "Start New Recording" next to a person to create a session already linked to them
- **During or after recording**: In the Speakers tab, assign speaker indexes to known people from your People list

---

## AI Features 🤖

### AI Chat
The AI chat lets you ask questions about your recordings in natural language. Instead of searching through transcripts, just ask "What were the main concerns?" or "Summarize the key action items" and get instant answers.

**How to use it:**
- Click the lightning bolt icon in the sidebar or navigate to "Ask"
- Type your question about any recording or collection
- The AI analyzes the content and provides a direct answer

### Automatic Analysis
After you finish recording, IntakeAI automatically:

- **Transcribes** your audio into searchable text
- **Identifies speakers** by detecting different voices
- **Generates insights** highlighting key points, action items, and important information
- **Creates summaries** tailored to different audiences (based on your template settings)

This all happens automatically—no manual work required. Just wait a few moments after stopping your recording.

### Speaker Management
The system automatically detects when different people are speaking, but you can improve accuracy by manually assigning speakers:

- **Auto-detection**: The system identifies different speakers and labels them as "Speaker 1", "Speaker 2", etc.
- **Manual assignment**: In the Speakers tab, link these speaker indexes to people from your People list
- **Continuity**: Once assigned, the system remembers these associations for future recordings

---

## Sharing & Distribution 📤

### Why Use Distribution Groups?
Distribution groups let you automatically send summaries to the right people. Instead of manually sharing each recording, you set up groups (like "Legal Team" or "Management") and the system sends the appropriate summaries to each group automatically.

### Setting Up Distribution Groups
Create groups for different audiences:

- **Define your groups**: Legal team, management, external stakeholders, etc.
- **Add email addresses**: Each group can have multiple recipients
- **Link to summaries**: Associate specific summary types with each group

When a recording is completed, summaries are automatically generated and sent to the relevant groups based on your template settings.

### Sharing Options
You can share recordings in several ways:

- **Individual Summaries**: Share specific AI-generated summaries with selected people
- **Complete Sessions**: Share the full recording, transcript, and all associated content
- **Filtered Content**: Share only specific portions or sections of a recording

### Approval Workflow
If your organization requires review before sharing, you can enable approval workflows:

- **Enable Approval**: Require sessions to be approved before summaries are distributed
- **Approval Status**: Track whether sessions are Pending, Approved, or Rejected
- **User Permissions**: Control which users have permission to approve content

---

## Troubleshooting 🔧

### Common Issues

#### Recording Not Starting
If the record button isn't working:

- **Check Permissions**: Your browser needs microphone access. Look for a permission prompt or check your browser settings
- **Browser Support**: Use Chrome, Firefox, or Safari for best compatibility
- **Connection**: Ensure you have internet connectivity (required for cloud processing)

#### Poor Audio Quality
For better transcription accuracy:

- **Microphone Position**: Keep your device close to the speakers
- **Background Noise**: Record in a quiet environment when possible
- **Multiple Speakers**: Have each person speak clearly and avoid talking over each other

#### Transcript Accuracy
To improve transcription quality:

- **Clear Speech**: Speak at a moderate pace and enunciate clearly
- **Technical Terms**: Spell out complex terms or names when first mentioned
- **Speaker Changes**: Pause briefly when switching between speakers

#### Missing Features
If something isn't showing up:

- **Refresh Page**: Try refreshing the browser—sometimes features need to reload
- **Browser Cache**: Clear your browser cache if issues persist
- **Session State**: Some features only appear after recording has started or completed

### Keyboard Shortcuts
Speed up your workflow with these shortcuts:

- **Space**: Play/Pause during audio playback
- **Esc**: End recording and trigger processing
- **Enter**: Send messages in AI chat
- **Search**: Use the search icon or global search to find content quickly

### Best Practices

#### Before Recording
1. Test your microphone to ensure it's working
2. Choose a template that matches your session type
3. Find a quiet location to minimize background noise
4. Prepare your questions or agenda in advance

#### During Recording
1. Speak clearly and at a natural pace
2. Identify speakers by name when they first speak
3. Pause briefly between topics for better organization
4. Use the notes feature to mark important moments

#### After Recording
1. Review the transcript for any errors
2. Assign speakers to people from your People list
3. Answer any template questions that weren't covered during recording
4. Review the AI-generated summaries before sharing
5. Share summaries with the appropriate distribution groups

---

## Advanced Features ⚡

### Template Logic (Coming Soon)
Future enhancements will make templates even more powerful:

- **Conditional Questions**: Questions will appear or hide based on previous answers, creating dynamic workflows
- **Dynamic Workflows**: Templates will adapt the conversation flow based on responses
- **Integration Rules**: Connect templates with external systems for automated data entry

### Analytics & Reporting
Track and analyze your recording activity:

- **Session Metrics**: View duration, speaker distribution, and topic analysis for each session
- **Trend Analysis**: Identify patterns across multiple sessions to spot trends
- **Performance Insights**: See which templates are most effective and how they're being used

### Integration Capabilities
Connect IntakeAI with other tools you use:

- **Calendar Integration**: Schedule recording sessions directly from your calendar
- **CRM Integration**: Link recordings to customer records in your CRM system
- **Document Management**: Organize files and transcripts with your existing document systems
- **Notification Systems**: Set up automated alerts and reminders for important events

---

## Support 💬

### Need Help?
The AI chat feature can answer any "how to" questions you might have about using IntakeAI. Click the lightning bolt icon in the sidebar or navigate to "Ask" to get started.

### Submit a Support Request
Have a bug to report, feature request, or need additional assistance? Use the form below to contact our support team.

---

*This help system is designed to grow with your needs. As new features are added, this documentation will be updated to reflect the latest capabilities.*`;

export default helpContent;

